/** @file Auto-rig panel layout: status line, imports, analysis list, argument form, Analyse, session status. */
import type { Translate } from '@pose2d/i18n/types';
import { useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { liveStore } from '../../stores/liveStore';
import { selectionStore } from '../../stores/selectionStore';
import { Button } from '../../ui-kit/Button';
import { ArgsForm } from './ArgsForm';
import { AutorigActions } from './AutorigActions';
import type { AutorigForm, AutorigSession } from './autorigTypes';
import { defaultArgs } from './defaultArgs';
import { docCounts } from './docCounts';
import { readyArgs } from './readyArgs';
import { ReviewDialog } from './ReviewDialog';
import { SessionStatus } from './SessionStatus';
import { useAutorigRequest } from './useAutorigRequest';
import './autorig.css';

/** Props of AutorigPanelBody; `imports` is the import buttons slot (null in tests). */
export interface AutorigPanelBodyProps {
  t: Translate;
  session: AutorigSession;
  imports: ReactNode;
}

/** The form starts on autoRig; picking an analysis refills its arguments from the current selection. */
export function AutorigPanelBody({ t, session, imports }: AutorigPanelBodyProps) {
  const doc = useStore(docStore, currentDoc);
  const selection = useStore(selectionStore, (s) => s);
  const view = useStore(session.store, (s) => s);
  const commits = useStore(liveStore, (s) => s.commits);
  const [form, setForm] = useState<AutorigForm>({ fn: 'autoRig', args: {} });
  useAutorigRequest({ doc, selection, session, setForm });
  if (doc === null) return null;
  const ready = readyArgs(form.fn, form.args);
  return (
    <div className="autorig-panel" data-testid="panel-autorig">
      <p className="autorig-counts">{t('autorig.status', docCounts(doc))}</p>
      {imports}
      <AutorigActions t={t} fn={form.fn} onPick={(fn) => { setForm({ fn, args: defaultArgs(fn, doc, selection) }); }} />
      <ArgsForm t={t} fn={form.fn} args={form.args} doc={doc} onChange={(args) => { setForm({ fn: form.fn, args }); }} />
      <Button primary label={t('autorig.analyse')} disabled={view.kind === 'running' || ready.missing.length > 0} onClick={() => { void session.run({ fn: form.fn, args: ready.args, pivots: {} }); }} />
      <SessionStatus t={t} view={view} session={session} commits={commits} />
      <ReviewDialog t={t} view={view} session={session} doc={doc} />
    </div>
  );
}
